import { createContext, useContext, useReducer, type ActionDispatch, type ReactNode } from 'react';
import type { UserSettings } from '../../types/auth';

type UserSettingsAction =
    | {
          type: 'set_user_settings_line_thickness';
          payload: number;
      }
    | {
          type: 'set_user_settings_color_style';
          payload: string;
      };

const reducer = (state: UserSettings, action: UserSettingsAction): UserSettings => {
    switch (action.type) {
        case 'set_user_settings_line_thickness':
            return {
                ...state,
                canvasSettings: {
                    lineThickness: action.payload,
                    mapElementColorStyle: state.canvasSettings.mapElementColorStyle,
                },
            };
        case 'set_user_settings_color_style':
            return {
                ...state,
                canvasSettings: {
                    lineThickness: state.canvasSettings.lineThickness,
                    mapElementColorStyle: action.payload,
                },
            };
        default:
            return state;
    }
};

type UserSettingsProviderProps = {
    userSettings: UserSettings;
    dispatchUserSettings?: (() => void) | undefined;
    children: ReactNode;
};

const UserSettingsContext = createContext<UserSettings | null>(null);
const UserSettingsDispatchContext = createContext<ActionDispatch<
    [action: UserSettingsAction]
> | null>(null);

const UserSettingsProvider = ({
    userSettings: initialUserSettings,
    dispatchUserSettings: mockedDispatch,
    children,
}: UserSettingsProviderProps) => {
    const [userSettings, dispatchUserSettings] = useReducer(reducer, initialUserSettings);

    return (
        <UserSettingsDispatchContext.Provider value={mockedDispatch ?? dispatchUserSettings}>
            <UserSettingsContext.Provider value={userSettings}>
                {children}
            </UserSettingsContext.Provider>
        </UserSettingsDispatchContext.Provider>
    );
};

const useUserSettings = (): UserSettings => {
    const context = useContext(UserSettingsContext);
    if (context === null) {
        throw new Error('useUserSettings must be used within a UserSettingsProvider');
    }
    return context;
};

const useUserSettingsDispatch = (): ActionDispatch<[action: UserSettingsAction]> => {
    const context = useContext(UserSettingsDispatchContext);
    if (context === null) {
        throw new Error('useUserSettingsDispatch must be used within a UserSettingsProvider');
    }
    return context;
};

export { UserSettingsProvider, useUserSettings, useUserSettingsDispatch };
